import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel, AccordionItemState } from 'react-accessible-accordion';
import { caseInsensitiveSortBy, verifyRepeatedTask } from '../../Components/action/utilities'
import _ from 'lodash'
import { setProjectDescription, setProjectTasks } from '../../redux/slices/ProjectSlice'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { fetchTaskList } from '../../Components/action/common'
import { FolderIcon, NoDateIcon, OverDueIcon, TasksListIcon, UpcomingTasksIcon } from '../../Components/LandingHeader/Feedback/icons/Icons';
import ArrowUpDn from './ArrowUpDn';
import TaskList from './TaskList';
import moment from 'moment';
import TasksStatusWidget from './TasksStatusWidget';
import TaskSourceIcon from './TaskSourceIcon';
import { updateProjectDesc } from '../../Components/action/tasks';

const ToggleAccordionArrow = ({ theme }) => {
    return (
      <AccordionItemState>
        {({ expanded }) => (
          expanded 
          ? 
          <span className={`accordion_arrow active flex items-center justify-center py-0 px-2`}>
            {
              theme == "dark" ? <ArrowUpDn/> : <img src="/images/arrow-up-dn.svg" alt="arrow" />
            }
          </span>
          : 
          <span className={`accordion_arrow flex items-center justify-center py-0 px-2`}>
            {
              theme == "dark" ? <ArrowUpDn/> : <img src="/images/arrow-up-dn.svg" alt="arrow" />
            }
          </span>
        )}
      </AccordionItemState>
    )
}

const ProjectTasks = () => {
    const [tasks, setTasks] = useState([])
    const [tasksSortBy, setTasksSortBy] = useState('date')
    const [tasksSortDirection, setTasksSortDirection] = useState(null)
    let { id, projectTitle, projectDescription } = useParams()
    projectTitle = projectTitle.replaceAll('_', ' ');
    const [projectDesc, setProjectDesc] = useState('')
    const [projectId, projectSource] = id.split('_');
    const dispatch = useDispatch()
    const {projectTasks, projectsListWithTasksCount} = useSelector(state => state.project)
    const theme = useSelector(state => state.theme.theme)
    const {settings} = useSelector(state => state.settings)
    const wrapperRef = useRef(null)

    useEffect(() => {
        setTasksSortBy(settings?.sortBy ?? 'date');
        setTasksSortDirection(settings?.sort ?? null);
    }, [settings])

    useEffect(() => {
        const bound = wrapperRef.current.getBoundingClientRect()
        wrapperRef.current.style.height = window.innerHeight - bound.top + 'px'
    }, [])

    useEffect(() => {
        if(!projectId) return;
        
        const fetchTasks = async () => {
            const response = await fetchTaskList({projectId, status: 0});
            dispatch(setProjectTasks(response?.data?.responseData ?? []));
        }
        fetchTasks();
    }, [projectId, projectTitle])

    useEffect(() => {
        setProjectDesc(projectDescription.trim())
    }, [projectDescription])

    useEffect(() => {
        if(!projectTasks) return;

        const today = moment().startOf('day').toISOString();
        // Overdue tasks
        const overdue = _.orderBy(projectTasks.map(item => {
        if(item?.repeat?.rule){
            const result = verifyRepeatedTask(item, null, [], true);
            return result?.length > 0 ? {...item, date: new Date(result[0]).toISOString()} : false;
        }else{
            const result = item.status == 0 && item.date !== null && item.date < today;
            return result ? item : false;
        }

        }).filter(item => item !== false), ['date'], ['desc']);

        // Upcoming tasks
        const upcoming = _.orderBy(projectTasks.map(item => {
        if(item?.repeat?.rule){
            const result = verifyRepeatedTask(item, null, [], false, true);
            return result?.length > 0 ? {...item, date: new Date(result[0]).toISOString()} : false;
        }else{
            const result = item.status == 0 && item.date !== null && item.date >= today;
            return result ? item : false;
        }

        }).filter(item => item !== false), ['date'], ['asc']);

        // No date tasks
        const noDate = projectTasks.filter(item => {
            return item.date === null;
        });

        // Set tasks
        setTasks([
            {
                title: "Overdue",
                tasks: tasksSortBy && tasksSortDirection ? caseInsensitiveSortBy(overdue, tasksSortBy, tasksSortDirection) : overdue,
                colorClass: "darkOverDueColor",
                count: overdue.length,
                icon: <OverDueIcon color={'var(--overDue-color)'} />
            },
            {
                title: "Upcoming",
                tasks: tasksSortBy && tasksSortDirection ? caseInsensitiveSortBy(upcoming, tasksSortBy, tasksSortDirection) : upcoming,
                colorClass: "darkPrimaryColor",
                count: upcoming.length,
                icon: <UpcomingTasksIcon color={`var(--accent-color)`} />
            },
            {
                title: "No Date",
                tasks: tasksSortBy && tasksSortDirection ? caseInsensitiveSortBy(noDate, tasksSortBy, tasksSortDirection) : noDate,
                colorClass: "darkPrimaryColor",
                count: noDate.length,
                icon: <NoDateIcon color={`var(--accent-color)`} />
            }
        ])

    }, [projectTasks, tasksSortBy, tasksSortDirection])

    const StatusWidget = () => {
        return useMemo(() => {
            const projects = projectsListWithTasksCount.filter(project => project.project_id == projectId)?.[0];
            const completedTasksCount = projects?.completedTasksCount ?? 0;
            const pendingTasksCount = projects?.pendingTasksCount ?? 0;
    
            return pendingTasksCount > 0 ? <TasksStatusWidget className="border mr-0 w-auto" direction="right" taskStatus={{completed: completedTasksCount, total: pendingTasksCount + completedTasksCount}}  /> : null
        }, [projectsListWithTasksCount])
    }

    const updateProjectDescription = async (e) => {
        e.stopPropagation();
        dispatch(setProjectDescription({project_id: projectId, description: e.target.value}))
        console.log({project_id: projectId, description: e.target.value})
        await updateProjectDesc(projectId, e.target.value)
    }

    return (
        <div ref={wrapperRef} id="tasks-list-wrapper" className="overflow-x-hidden overflow-y-auto dark:!border-none rounded-[26px]">
            <div className="upcoming-form">
                <div className="accordion-view">
                    <Accordion allowZeroExpanded allowMultipleExpanded preExpanded={['accitem']}>
                        <AccordionItem uuid={`accitem`} className={`accordion-item ${!projectTasks.length ? 'no-task' : ''}`}>
                            <AccordionItemHeading>
                                <AccordionItemButton>
                                {
                                    <div className={`accordion_tab cursor-pointer !items-start !justify-start !gap-[20px] !flex-col bg-white dark:bg-black`}>
                                        <div className="flex items-center justify-between w-full">
                                            <div className="heading w-full">
                                                <FolderIcon color={`var(--accent-color)`} fill={`var(--accent-color)`} />
                                                <h3 className="accordion-title darkPrimaryColor">{projectTitle}</h3>
                                                <TaskSourceIcon task={{id, taskSource: projectSource}} colorPicker={`var(--accent-color)`} />
                                                <span className="flex-1 self-stretch"></span>
                                                <StatusWidget />
                                            </div>
                                            <ToggleAccordionArrow theme={theme} />
                                        </div>
                                        <div className="w-full flex items-center justify-start">
                                            <input onKeyDown={e => e.stopPropagation()} onBlur={updateProjectDescription} onClick={e => e.stopPropagation()} type="text" placeholder="Add description..." className="w-full font-inter font-normal outline-[0px] bg-transparent dark:text-white" onInput={e => setProjectDesc(e.target.value)} value={projectDesc} />
                                        </div>
                                    </div>
                                }
                                </AccordionItemButton>
                            </AccordionItemHeading>
                            {
                                <AccordionItemPanel>
                                    <Accordion allowZeroExpanded allowMultipleExpanded preExpanded={[`accitem0`, `accitem1`]}>
                                    {
                                        tasks.map((group, index) => {
                                            return (
                                                    <AccordionItem key={index} uuid={`accitem${index}`} className={`accordion-item ${!group.tasks.length ? 'no-task' : ''}`}>
                                                        <AccordionItemHeading>
                                                            <AccordionItemButton>
                                                                <div className={`accordion_tab cursor-pointer bg-white dark:bg-black !pb-5`}>
                                                                    <div className="heading w-full">
                                                                        {group.icon}
                                                                        <h3 className={`accordion-title ${group.colorClass} font text-xl`}>{group.title}</h3>
                                                                        {
                                                                            group.tasks.length > 0
                                                                            &&
                                                                            <>
                                                                                <span className="flex-1 self-stretch"></span>
                                                                                <span className="py-0 px-[14px] font text-xs text-black dark:text-white font-inter opacity-50">{`${group.tasks.length} Task${group.tasks.length > 1 ? 's' : ''}`}</span>
                                                                            </>
                                                                        }
                                                                        
                                                                    </div>
                                                                    <ToggleAccordionArrow theme={theme} />
                                                                </div>
                                                                {index < 2 && <div className="hr-line"></div>}
                                                            </AccordionItemButton>
                                                        </AccordionItemHeading>
                                                        <AccordionItemPanel>
                                                        {
                                                            group.tasks.map(task => {
                                                                return (
                                                                    <TaskList projectId={projectId} task={task} key={`item-${task.id}${task.updatedAt}`} disableSort={true}  currentPage="home" groupDate={task.date} showDate={true} />
                                                                )
                                                            })
                                                        }
                                                        </AccordionItemPanel>
                                                    </AccordionItem>
                                            )
                                        })
                                    }    
                                    </Accordion>
                                </AccordionItemPanel>
                            }
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </div>
    )
}

export default ProjectTasks
